/**
 * Badge & Trophy System Types
 * Used for achievement badges displayed on user profiles
 */

// Badge visual shape types
export type BadgeShape = 'shield' | 'circle' | 'hex';

// Badge tier levels mapped from numeric levels
export type BadgeTier = 'bronze' | 'silver' | 'gold' | 'diamond' | 'mythic';

// Map numeric level (1-5) to tier name
export const TIER_FROM_LEVEL: Record<number, BadgeTier> = {
  1: 'bronze',
  2: 'silver',
  3: 'gold',
  4: 'diamond',
  5: 'mythic',
};

// Tier color palette
export const TIER_COLORS: Record<BadgeTier, string> = {
  bronze: '#CD7F32',
  silver: '#C0C0C0',
  gold: '#FFD700',
  diamond: '#B9F2FF',
  mythic: '#EE5000',
};

// Tier glow colors (same hue, 0.6 opacity)
export const TIER_GLOW_COLORS: Record<BadgeTier, string> = {
  bronze: 'rgba(205, 127, 50, 0.6)',
  silver: 'rgba(192, 192, 192, 0.6)',
  gold: 'rgba(255, 215, 0, 0.6)',
  diamond: 'rgba(185, 242, 255, 0.6)',
  mythic: 'rgba(238, 80, 0, 0.6)',
};

// GET /api/v1/badges — individual badge definition
export interface Badge {
  id: number;
  slug: string;
  title: string;
  subtitle: string;
  icon: string;
  shape: BadgeShape;
  effect: string | null;
  level: number;
  category: string;
  criteria: string;
  criteriaValue: number | null;
  notes: string | null;
  isCompetitive: boolean;
  createdAt: string;
  currentHolder?: { userId: number; username: string; avatarUrl: string | null } | null;
}

// GET /api/v1/badges/users/:userId — earned badge record
export interface UserBadge {
  id: number;
  userId: number;
  badgeId: number;
  badge: Badge;
  earnedAt: string;
  isDisplayed: boolean;
  displayOrder: number | null;
  /**
   * Showcase auto-fill (Asana 1219948165170031): true when the backend placed this
   * earned badge in an empty slot; the owner never picked it. Only present on
   * showcase lists (hover card, badge summary), never on the raw earned list.
   */
  autoFilled?: boolean;
}

// GET /api/v1/badges/users/:userId/summary
export interface BadgeSummary {
  displayedBadges: UserBadge[];
  maxBadgeSlots: number;
}

// PUT /api/v1/badges/display
export interface UpdateBadgeDisplayRequest {
  badges: { badgeId: number; order: number }[];
}

// Badge progress data for own-profile tracking
export interface BadgeProgress {
  cards_sent: number;
  cards_received: number;
  max_package_value_sent: number;
  unique_trade_partners: number;
  total_trade_value: number;
  consecutive_trading_days: number;
  avg_ship_time_days: number | null;
  total_trades: number;
}

// Socket event: badge:earned
export interface BadgeEarnedEvent {
  badge: {
    id: number;
    slug: string;
    title: string;
    subtitle: string;
    icon: string;
    shape: BadgeShape;
    effect: string | null;
    level: number;
    category: string;
  };
  earnedAt: string;
}

// Socket event: badge:lost
export interface BadgeLostEvent {
  badge: {
    id: number;
    slug: string;
    title: string;
  };
}
